import { isLang, lang } from '@umu/ukit-lang';
import { getShareV2 } from '@umu/umu-api';
import { SnackBar } from '@umu/umu-design';
import { addQueryString, openWindow } from '@umu/umu-dom';
import { ShareType, SWITCH_STATUS } from '@umu/umu-types';
import dayjs from 'dayjs';
import merge from 'lodash/merge';
import { CertificateReceiveStatus, type ICaetSwitchInfo } from '../interface/api';
import type { IDownloadCaetCertificateData, IDownloadCaetResult } from '../interface/utils';
import { downLinkByWeb } from '../../../../../@utils/umuUtil';
/***
 * @description: 是否是AI力资源 [课程]
 */
const isCaetCourse = (switchInfo: ICaetSwitchInfo) => {
    return Number(switchInfo?.is_caet_obj) === SWITCH_STATUS.YES;
};
/**
 * @description: 是否展示AI力认证证书[仅资源使用]
 * @param isEnterprise 是否是企业
 * @param switchInfo 企业配置开关信息
 * @returns 是否展示AI力认证证书
 * 资源：C端用户，如果资源是AI力资源【is_caet_obj】则展示， B端用户，除了资源外，需要依据企业开关【switch】和资源开关【is_caet_obj】是否都为开启状态，都开启才展示
 */
const defaultSwitchInfo: ICaetSwitchInfo = {
    switch: SWITCH_STATUS.NO,
    is_caet_obj: SWITCH_STATUS.NO,
};
const isShowCaet = (isEnterprise: boolean, switchInfo = defaultSwitchInfo) => {
    if (!isEnterprise) {
        return Number(switchInfo?.is_caet_obj) === SWITCH_STATUS.YES;
    }
    const tmpSwitchInfo: ICaetSwitchInfo = merge({}, defaultSwitchInfo, switchInfo);
    return [tmpSwitchInfo.switch, tmpSwitchInfo.is_caet_obj].every((item) => Number(item) === SWITCH_STATUS.YES);
};

/**
 * 下载单个AI力认证证书
 */
const downloadCaetCertificate = async (data: IDownloadCaetCertificateData): Promise<IDownloadCaetResult> => {
    const { id, certificate_id = '', exportType = 'pdf', fileName = '', directExport = true } = data;
    try {
        const result = await getShareV2({
            obj_id: id,
            certificate_id: certificate_id,
            obj_type: ShareType.AI_POWER_RECOGNITION_CERTIFICATE,
            export_type: exportType,
        });
        const link: string = result?.data?.result_url || '';
        const isSuccess = Number(result.data.status) === 4; // 4 表示导出成功
        if (directExport && link && isSuccess) {
            const downType = link.split('.').pop();
            downLinkByWeb(link, `${fileName || certificate_id || id || 'caet_certificate'}.${downType}`);
            SnackBar.show({
                message: lang('已下载'),
            });
        }
        return {
            resultUrl: result?.data?.result_url || '',
            isSuccess,
        };
    } catch (error) {
        return {
            resultUrl: '',
            isSuccess: false,
        };
    }
};
/**
 * 重定向到领英新页面
 */
interface IAddCaetData {
    link?: string;
    startTask?: string;
    organizationId?: string;
    params?: Record<string, string>;
}
enum LINKEDIN_START_TASK {
    CERTIFICATION_NAME = 'CERTIFICATION_NAME', // 新增证书页面
}
const LINKEDIN_DOMAIN = 'https://www.linkedin.com'; // 领英域名
const LINKEDIN_ADD_CERTIFICATE_URL = `${LINKEDIN_DOMAIN}/profile/add`; // 领英新增证书页面
const UMU_LINKEDIN_ORGANIZATION_ID = '7945874'; // UMU 领英组织ID

const redirectToLinkedin = (data: IAddCaetData, isOpenWindow = false): string => {
    const {
        link = LINKEDIN_ADD_CERTIFICATE_URL,
        organizationId = UMU_LINKEDIN_ORGANIZATION_ID,
        params,
        startTask = LINKEDIN_START_TASK.CERTIFICATION_NAME,
    } = data;
    const tmpUrl = addQueryString(link, {
        ...params,
        startTask,
        organizationId,
    });
    if (isOpenWindow) {
        openWindow(tmpUrl, '_blank');
    }
    return tmpUrl;
};
const getCaetDefaultFormat = (): string => {
    if (isLang('CN') || isLang('TW')) {
        return 'MM 月 DD, YYYY';
    }
    if (isLang('JP')) {
        return 'YYYY.MM.DD';
    }
    if (isLang('EN')) {
        return 'MMM DD, YYYY';
    }
    return 'MM/DD/YYYY';
};
const onTransformCertificateDate = (date: string, format = getCaetDefaultFormat()) => {
    const durationNum = Number(date);
    if (isNaN(durationNum) || durationNum <= 0) {
        return '';
    }
    return dayjs(durationNum * 1000).format(format);
};
const isReceiveCertificate = (status?: CertificateReceiveStatus) => {
    return Number(status) === CertificateReceiveStatus.RECEIVED;
};
export {
    downloadCaetCertificate,
    isCaetCourse,
    isReceiveCertificate,
    isShowCaet,
    LINKEDIN_ADD_CERTIFICATE_URL,
    LINKEDIN_START_TASK,
    onTransformCertificateDate,
    redirectToLinkedin,
    UMU_LINKEDIN_ORGANIZATION_ID,
};
